import React, { useState, useEffect } from 'react'
import {
  BookOpen, Plus, Filter, Search, Tag, Users, Calendar, CheckCircle, AlertCircle,
  Lightbulb, Target, Award, ChevronRight, Star, FileText, X, Layers, Clock,
  Building2, Sparkles, Download, Grid, List, Bookmark, AlertTriangle
} from 'lucide-react'
import { projectsService, Project } from '../services/projects'
import { lessonsLearnedService, LessonLearned as LessonLearnedData, LessonLearnedCreate } from '../services/lessonsLearned'
import { toast } from 'sonner'
import { getErrorMessage } from '../utils/apiTransformers'
import type { ApiErrorLike } from '../utils/apiError'

interface Lesson {
  id: string
  title: string
  project: string
  projectId: string
  category: string
  type: 'success' | 'challenge' | 'improvement' | 'innovation'
  phase: string
  date: string
  author: string
  impact: number
  status: 'implemented' | 'shared' | 'action-pending' | 'archived'
  views: number
  bookmarks: number
  description: string
  actionTaken?: string
  benefitsRealized?: string
  tags: string[]
  relatedLessons?: string[]
}

const EMPTY_FORM: LessonLearnedCreate = {
  title: '',
  description: '',
  category: '',
  type: '',
  phase: '',
  impact: undefined,
  status: 'shared',
  action_taken: '',
  benefits_realized: '',
  tags: [],
  project_id: undefined,
  program_id: undefined,
}

export default function LessonsLearned() {
  const [projects, setProjects] = useState<Project[]>([])
  const [lessonsData, setLessonsData] = useState<LessonLearnedData[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedType, setSelectedType] = useState('all')
  const [selectedProject, setSelectedProject] = useState('all')
  const [selectedImpact, setSelectedImpact] = useState('all')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [showFilters, setShowFilters] = useState(false)
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createForm, setCreateForm] = useState<LessonLearnedCreate>(EMPTY_FORM)
  const [tagInput, setTagInput] = useState('')
  const [saving, setSaving] = useState(false)
  // Sprint 15 — drives the lesson detail modal opened by the "View
  // Details" / "View Full Entry" buttons on each lesson card.
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [projectsData, lessonsResult] = await Promise.all([
        projectsService.list(),
        lessonsLearnedService.list(),
      ])
      setProjects(projectsData)
      setLessonsData(lessonsResult)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error loading data:', error)
      toast.error(getErrorMessage(error) || 'Failed to load lessons learned data')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!createForm.title.trim() || !createForm.description.trim()) {
      toast.error('Title and description are required')
      return
    }
    setSaving(true)
    try {
      await lessonsLearnedService.create({
        ...createForm,
        impact: createForm.impact ? Number(createForm.impact) : undefined,
        tags: createForm.tags?.filter(Boolean),
      })
      toast.success('Lesson learned added successfully')
      setShowCreateModal(false)
      setCreateForm(EMPTY_FORM)
      setTagInput('')
      loadData()
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      toast.error(getErrorMessage(error) || 'Failed to create lesson')
    } finally {
      setSaving(false)
    }
  }

  const addTag = () => {
    const tag = tagInput.trim()
    if (tag && !createForm.tags?.includes(tag)) {
      setCreateForm(prev => ({ ...prev, tags: [...(prev.tags || []), tag] }))
      setTagInput('')
    }
  }

  const removeTag = (tag: string) => {
    setCreateForm(prev => ({ ...prev, tags: (prev.tags || []).filter(t => t !== tag) }))
  }

  // Map backend data to Lesson interface
  const lessons: Lesson[] = lessonsData.map(item => ({
    id: `LL-${item.id}`,
    title: item.title,
    project: projects.find(p => p.id === item.project_id)?.name || 'No project',
    projectId: item.project_id?.toString() || '',
    category: item.category || 'general',
    type: (item.type as Lesson['type']) || 'improvement',
    phase: item.phase || '',
    date: item.created_at.split('T')[0],
    author: item.author?.full_name || item.author?.email || 'Unknown',
    impact: item.impact || 5,
    status: (item.status as Lesson['status']) || 'shared',
    views: 0,
    bookmarks: 0,
    description: item.description,
    actionTaken: item.action_taken || undefined,
    benefitsRealized: item.benefits_realized || undefined,
    tags: item.tags || [],
  }))

  // Portfolio-level lessons learned statistics
  const stats = {
    totalLessons: lessons.length,
    positiveOutcomes: lessons.filter(l => l.type === 'success').length,
    challenges: lessons.filter(l => l.type === 'challenge').length,
    implementedChanges: lessons.filter(l => l.status === 'implemented').length,
    avgImpactScore: lessons.length > 0 
      ? (lessons.reduce((sum, l) => sum + l.impact, 0) / lessons.length).toFixed(1)
      : '0.0'
  }

  // Knowledge base categories
  const categories = [
    { id: 'all', name: 'All Topics', icon: Grid, count: lessons.length, color: 'bg-gray-500' },
    { id: 'technical', name: 'Technical & Architecture', icon: Layers, count: lessons.filter(l => l.category === 'technical').length, color: 'bg-blue-500' },
    { id: 'process', name: 'Process & Methodology', icon: Target, count: lessons.filter(l => l.category === 'process').length, color: 'bg-purple-500' },
    { id: 'communication', name: 'Communication & Stakeholders', icon: Users, count: lessons.filter(l => l.category === 'communication').length, color: 'bg-green-500' },
    { id: 'risk', name: 'Risk & Issue Management', icon: AlertTriangle, count: lessons.filter(l => l.category === 'risk').length, color: 'bg-red-500' },
    { id: 'resource', name: 'Resource & Team Management', icon: Users, count: lessons.filter(l => l.category === 'resource').length, color: 'bg-orange-500' },
    { id: 'procurement', name: 'Procurement & Vendors', icon: Building2, count: lessons.filter(l => l.category === 'procurement').length, color: 'bg-indigo-500' },
    { id: 'quality', name: 'Quality & Testing', icon: CheckCircle, count: lessons.filter(l => l.category === 'quality').length, color: 'bg-emerald-500' },
    { id: 'schedule', name: 'Schedule & Planning', icon: Clock, count: lessons.filter(l => l.category === 'schedule').length, color: 'bg-yellow-500' }
  ]

  // Type filters
  const typeFilters = [
    { id: 'all', name: 'All Types', count: lessons.length },
    { id: 'success', name: 'Success Stories', count: lessons.filter(l => l.type === 'success').length, icon: CheckCircle, color: 'text-green-600' },
    { id: 'challenge', name: 'Challenges & Issues', count: lessons.filter(l => l.type === 'challenge').length, icon: AlertCircle, color: 'text-orange-600' },
    { id: 'improvement', name: 'Process Improvements', count: lessons.filter(l => l.type === 'improvement').length, icon: Lightbulb, color: 'text-blue-600' },
    { id: 'innovation', name: 'Innovations', count: lessons.filter(l => l.type === 'innovation').length, icon: Sparkles, color: 'text-purple-600' }
  ]

  // Impact filters
  const impactFilters = [
    { id: 'all', name: 'All Impact Levels' },
    { id: 'high', name: 'High Impact (8-10)', color: 'text-red-600' },
    { id: 'medium', name: 'Medium Impact (5-7)', color: 'text-orange-600' },
    { id: 'low', name: 'Low Impact (1-4)', color: 'text-blue-600' }
  ]

  // Projects for filtering
  const projectOptions = [
    { id: 'all', name: 'All Projects' },
    ...projects.map(p => ({ id: p.id.toString(), name: p.name }))
  ]

  // Popular tags
  const popularTags = Array.from(new Set(lessons.flatMap(l => l.tags))).slice(0, 12)

  // Filter functions
  const toggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag))
    } else {
      setSelectedTags([...selectedTags, tag])
    }
  }

  const clearAllFilters = () => {
    setSelectedCategory('all')
    setSelectedType('all')
    setSelectedProject('all')
    setSelectedImpact('all')
    setSelectedTags([])
    setSearchTerm('')
  }

  const getImpactCategory = (impact: number) => {
    if (impact >= 8) return 'high'
    if (impact >= 5) return 'medium'
    return 'low'
  }

  const filteredLessons = lessons.filter(lesson => {
    const matchesSearch = searchTerm === '' || 
      lesson.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lesson.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lesson.project.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lesson.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    
    const matchesCategory = selectedCategory === 'all' || lesson.category === selectedCategory
    const matchesType = selectedType === 'all' || lesson.type === selectedType
    const matchesProject = selectedProject === 'all' || lesson.projectId === selectedProject
    const matchesImpact = selectedImpact === 'all' || getImpactCategory(lesson.impact) === selectedImpact
    const matchesTags = selectedTags.length === 0 || selectedTags.every(tag => lesson.tags.includes(tag))
    
    return matchesSearch && matchesCategory && matchesType && matchesProject && matchesImpact && matchesTags
  })

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'success': return <CheckCircle className="w-4 h-4" />
      case 'challenge': return <AlertCircle className="w-4 h-4" />
      case 'improvement': return <Lightbulb className="w-4 h-4" />
      case 'innovation': return <Sparkles className="w-4 h-4" />
      default: return <Target className="w-4 h-4" />
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'success': return 'bg-green-100 text-green-700 border-green-300'
      case 'challenge': return 'bg-orange-100 text-orange-700 border-orange-300'
      case 'improvement': return 'bg-blue-100 text-blue-700 border-blue-300'
      case 'innovation': return 'bg-purple-100 text-purple-700 border-purple-300'
      default: return 'bg-gray-100 text-gray-700 border-gray-300'
    }
  }

  const getStatusBadge = (status: string) => {
    const badges = {
      'implemented': { label: 'Implemented', class: 'bg-green-100 text-green-700 border-green-300' },
      'shared': { label: 'Shared', class: 'bg-blue-100 text-blue-700 border-blue-300' },
      'action-pending': { label: 'Action Pending', class: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
      'archived': { label: 'Archived', class: 'bg-gray-100 text-gray-600 border-gray-300' }
    }
    return badges[status as keyof typeof badges] || badges.archived
  }

  const activeFiltersCount = 
    (selectedCategory !== 'all' ? 1 : 0) +
    (selectedType !== 'all' ? 1 : 0) +
    (selectedProject !== 'all' ? 1 : 0) +
    (selectedImpact !== 'all' ? 1 : 0) +
    selectedTags.length

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#E8E9E9]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#334E68] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading lessons learned...</p>
        </div>
      </div>
    )
  }

  return (
    <>
    <div className="min-h-screen bg-[#E8E9E9]">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#2A3B4C] via-[#334E68] to-[#2A3B4C] border-b-4 border-[#DAA520] px-8 py-8">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl text-white mb-2 flex items-center gap-3">
                <BookOpen className="w-8 h-8 text-[#DAA520]" />
                Lessons Learned Knowledge Base
              </h1>
              <p className="text-[#bcccdc] text-lg">
                Searchable repository of organizational knowledge and continuous improvement insights
              </p>
            </div>
            <div className="flex gap-3">
              <button className="px-6 py-3 bg-gradient-to-r from-[#486581] to-[#334E68] text-white rounded-lg hover:from-[#334E68] hover:to-[#2A3B4C] transition-all border border-[#DAA520]/30 flex items-center gap-2">
                <Download className="w-5 h-5" />
                Export Knowledge Base
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-gradient-to-r from-[#DAA520] to-[#c99a1c] text-white rounded-lg hover:from-[#c99a1c] hover:to-[#b38a19] transition-all shadow-lg flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Add New Lesson
              </button>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 bg-[#DAA520]/20 rounded-lg">
                  <BookOpen className="w-6 h-6 text-[#DAA520]" />
                </div>
              </div>
              <div className="text-3xl text-white mb-1">{stats.totalLessons}</div>
              <div className="text-sm text-[#bcccdc]">Total Lessons</div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                </div>
              </div>
              <div className="text-3xl text-white mb-1">{stats.positiveOutcomes}</div>
              <div className="text-sm text-[#bcccdc]">Success Stories</div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 bg-orange-500/20 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-orange-400" />
                </div>
              </div>
              <div className="text-3xl text-white mb-1">{stats.challenges}</div>
              <div className="text-sm text-[#bcccdc]">Challenges Documented</div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Target className="w-6 h-6 text-blue-400" />
                </div>
              </div>
              <div className="text-3xl text-white mb-1">{stats.implementedChanges}</div>
              <div className="text-sm text-[#bcccdc]">Changes Implemented</div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Award className="w-6 h-6 text-purple-400" />
                </div>
              </div>
              <div className="text-3xl text-white mb-1">{stats.avgImpactScore}</div>
              <div className="text-sm text-[#bcccdc]">Avg Impact Score</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-8 py-8">
        {/* Search and Filters */}
        <div className="mb-8 space-y-6">
          <div className="bg-[#334E68] rounded-xl p-6">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#829ab1]" />
                <input
                  type="text"
                  placeholder="Search by title, description, tags, or project name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-[#2A3B4C] text-white placeholder-[#829ab1] rounded-lg border border-[#486581] focus:border-[#DAA520] focus:outline-none text-lg"
                />
              </div>
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className={`px-6 py-3 rounded-lg transition-all flex items-center gap-2 ${
                  showFilters || activeFiltersCount > 0
                    ? 'bg-[#DAA520] text-white'
                    : 'bg-[#486581] text-white hover:bg-[#334E68]'
                }`}
              >
                <Filter className="w-5 h-5" />
                Advanced Filters
                {activeFiltersCount > 0 && (
                  <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
              <button 
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                className="px-6 py-3 bg-[#486581] text-white rounded-lg hover:bg-[#334E68] transition-all flex items-center gap-2"
              >
                {viewMode === 'grid' ? <List className="w-5 h-5" /> : <Grid className="w-5 h-5" />}
              </button>
            </div>

            {/* Active Filters */}
            {activeFiltersCount > 0 && (
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <span className="text-sm text-[#bcccdc]">Active filters:</span>
                {selectedCategory !== 'all' && (
                  <span className="px-3 py-1 bg-[#DAA520] text-white rounded-full text-sm flex items-center gap-2">
                    {categories.find(c => c.id === selectedCategory)?.name}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedCategory('all')} />
                  </span>
                )}
                {selectedTags.map(tag => (
                  <span key={tag} className="px-3 py-1 bg-[#DAA520] text-white rounded-full text-sm flex items-center gap-2">
                    {tag}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => toggleTag(tag)} />
                  </span>
                ))}
                <button 
                  onClick={clearAllFilters}
                  className="ml-2 text-sm text-[#DAA520] hover:text-[#c99a1c]"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>

          {/* Advanced Filters Panel */}
          {showFilters && (
            <div className="bg-white rounded-xl border-2 border-[#334E68] p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Lesson Type</label>
                <div className="grid grid-cols-5 gap-3">
                  {typeFilters.map((type) => {
                    const Icon = type.icon
                    return (
                      <button
                        key={type.id}
                        onClick={() => setSelectedType(type.id)}
                        className={`p-3 rounded-lg transition-all text-left ${
                          selectedType === type.id
                            ? 'bg-[#334E68] text-white border-2 border-[#DAA520]'
                            : 'bg-gray-50 text-gray-700 border-2 border-gray-200 hover:border-[#334E68]'
                        }`}
                      >
                        {Icon && (
                          <Icon className={`w-5 h-5 mb-2 ${selectedType === type.id ? 'text-[#DAA520]' : type.color}`} />
                        )}
                        <div className="text-sm font-medium">{type.name}</div>
                        <div className="text-xs opacity-70 mt-1">{type.count} lessons</div>
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Project</label>
                  <select
                    value={selectedProject}
                    onChange={(e) => setSelectedProject(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#334E68] focus:outline-none"
                  >
                    {projectOptions.map(project => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Impact Level</label>
                  <select
                    value={selectedImpact}
                    onChange={(e) => setSelectedImpact(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#334E68] focus:outline-none"
                  >
                    {impactFilters.map(impact => (
                      <option key={impact.id} value={impact.id}>{impact.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {popularTags.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Filter by Tags</label>
                  <div className="flex flex-wrap gap-2">
                    {popularTags.map(tag => (
                      <button
                        key={tag}
                        onClick={() => toggleTag(tag)}
                        className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                          selectedTags.includes(tag)
                            ? 'bg-[#334E68] text-white border-2 border-[#DAA520]'
                            : 'bg-gray-100 text-gray-700 border-2 border-gray-200 hover:border-[#334E68]'
                        }`}
                      >
                        <Tag className="w-3 h-3 inline mr-1" />
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Category Navigation */}
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-4">
            {categories.map((category) => {
              const Icon = category.icon
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`p-4 rounded-xl transition-all ${
                    selectedCategory === category.id
                      ? 'bg-gradient-to-br from-[#DAA520] to-[#c99a1c] text-white shadow-xl scale-105 border-2 border-[#DAA520]'
                      : 'bg-[#334E68] text-[#bcccdc] hover:bg-[#486581] border-2 border-[#486581]'
                  }`}
                >
                  <Icon className={`w-6 h-6 mx-auto mb-2 ${selectedCategory === category.id ? 'text-white' : 'text-[#bcccdc]'}`} />
                  <div className="text-xs font-medium mb-1 line-clamp-2">{category.name}</div>
                  <div className="text-xs opacity-80">{category.count}</div>
                </button>
              )
            })}
          </div>
        </div>

        {/* Results Summary */}
        <div className="mb-6">
          <h3 className="text-xl text-gray-900">
            Found {filteredLessons.length} {filteredLessons.length === 1 ? 'lesson' : 'lessons'}
          </h3>
        </div>

        {/* Lessons Display */}
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredLessons.map((lesson) => (
              <div key={lesson.id} className="bg-white rounded-xl border-2 border-gray-200 hover:border-[#DAA520] transition-all p-6 group">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-1 rounded-md text-xs border flex items-center gap-1 ${getTypeColor(lesson.type)}`}>
                        {getTypeIcon(lesson.type)}
                        {lesson.type.charAt(0).toUpperCase() + lesson.type.slice(1)}
                      </span>
                      <span className={`px-2 py-1 rounded-md text-xs border ${getStatusBadge(lesson.status).class}`}>
                        {getStatusBadge(lesson.status).label}
                      </span>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 group-hover:text-[#334E68] transition-colors line-clamp-2">
                      {lesson.title}
                    </h3>
                  </div>
                  
                  <div className="text-right ml-4">
                    <div className="text-2xl font-medium text-[#DAA520]">{lesson.impact}</div>
                    <div className="text-xs text-gray-500">Impact</div>
                    <div className="flex mt-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-3 h-3 ${
                            i < Math.round(lesson.impact / 2)
                              ? 'text-yellow-400 fill-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-xs text-gray-600 mb-4 pb-4 border-b border-gray-200">
                  <span className="flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    {lesson.project}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {lesson.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {lesson.date}
                  </span>
                </div>

                <p className="text-sm text-gray-700 mb-4 line-clamp-3">
                  {lesson.description}
                </p>

                {lesson.actionTaken && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="text-xs font-medium text-blue-900 mb-1">Action Taken</div>
                    <p className="text-xs text-blue-800 line-clamp-2">{lesson.actionTaken}</p>
                  </div>
                )}

                {lesson.benefitsRealized && (
                  <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="text-xs font-medium text-green-900 mb-1">Benefits Realized</div>
                    <p className="text-xs text-green-800">{lesson.benefitsRealized}</p>
                  </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-2 flex-wrap">
                    {lesson.tags.slice(0, 3).map((tag, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded flex items-center gap-1">
                        <Tag className="w-2.5 h-2.5" />
                        {tag}
                      </span>
                    ))}
                    {lesson.tags.length > 3 && (
                      <span className="text-xs text-gray-500">+{lesson.tags.length - 3} more</span>
                    )}
                  </div>

                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Bookmark className="w-3 h-3" />
                      {lesson.bookmarks}
                    </span>
                    <button
                      onClick={() => setSelectedLessonId(lesson.id)}
                      className="text-[#334E68] hover:text-[#DAA520] flex items-center gap-1"
                    >
                      View Details <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredLessons.map((lesson) => (
              <div key={lesson.id} className="bg-white rounded-xl border-2 border-gray-200 hover:border-[#DAA520] transition-all p-6 group">
                <div className="flex items-start gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-medium text-[#DAA520] mb-1">{lesson.impact}</div>
                    <div className="text-xs text-gray-500 mb-2">Impact</div>
                    <div className="flex flex-col">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-3 h-3 mx-auto ${
                            i < Math.round(lesson.impact / 2)
                              ? 'text-yellow-400 fill-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 rounded-md text-xs border flex items-center gap-1 ${getTypeColor(lesson.type)}`}>
                            {getTypeIcon(lesson.type)}
                            {lesson.type.charAt(0).toUpperCase() + lesson.type.slice(1)}
                          </span>
                          <span className={`px-2 py-1 rounded-md text-xs border ${getStatusBadge(lesson.status).class}`}>
                            {getStatusBadge(lesson.status).label}
                          </span>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 group-hover:text-[#334E68] transition-colors mb-2">
                          {lesson.title}
                        </h3>
                        <div className="flex items-center gap-4 text-xs text-gray-600 mb-3">
                          <span className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            {lesson.project}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {lesson.author}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {lesson.date}
                          </span>
                        </div>
                      </div>
                    </div>

                    <p className="text-sm text-gray-700 mb-4 bg-gray-50 rounded p-3 line-clamp-3">
                      {lesson.description}
                    </p>

                    {lesson.actionTaken && (
                      <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="text-xs font-medium text-blue-900 mb-1">Action Taken</div>
                        <p className="text-sm text-blue-800 line-clamp-2">{lesson.actionTaken}</p>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <div className="flex items-center gap-2 flex-wrap flex-1">
                        {lesson.tags.map((tag, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded flex items-center gap-1">
                            <Tag className="w-2.5 h-2.5" />
                            {tag}
                          </span>
                        ))}
                      </div>
                      <button
                        onClick={() => setSelectedLessonId(lesson.id)}
                        className="ml-4 text-[#334E68] hover:text-[#DAA520] flex items-center gap-1 text-sm"
                      >
                        View Full Entry <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No Results */}
        {filteredLessons.length === 0 && (
          <div className="text-center py-16">
            <div className="inline-block p-6 bg-gray-100 rounded-full mb-4">
              <Search className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-xl text-gray-900 mb-2">No lessons found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your search or filters</p>
            <button 
              onClick={clearAllFilters}
              className="px-6 py-3 bg-[#334E68] text-white rounded-lg hover:bg-[#486581] transition-all"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>
    </div>

    {/* Sprint 15 — Lesson detail modal. The card "View Details" /
        "View Full Entry" buttons open this. Shows the long-form
        fields (description, action taken, benefits realized, tags)
        that don't fit in the summary card. */}
    {selectedLessonId !== null && (() => {
      const lesson = lessons.find(l => l.id === selectedLessonId)
      if (!lesson) return null
      return (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedLessonId(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`Details for ${lesson.title}`}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="text-xl font-semibold text-gray-900 mb-1">{lesson.title}</h2>
                <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                  <span className="capitalize">{lesson.type}</span>
                  <span>·</span>
                  <span>{lesson.category}</span>
                  {lesson.phase && (<><span>·</span><span>{lesson.phase}</span></>)}
                  <span>·</span>
                  <span>{lesson.author}</span>
                </div>
              </div>
              <button
                onClick={() => setSelectedLessonId(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <section>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Description</h3>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{lesson.description}</p>
              </section>
              {lesson.actionTaken && (
                <section>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Action Taken</h3>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{lesson.actionTaken}</p>
                </section>
              )}
              {lesson.benefitsRealized && (
                <section>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Benefits Realized</h3>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{lesson.benefitsRealized}</p>
                </section>
              )}
              {lesson.tags && lesson.tags.length > 0 && (
                <section>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Tags</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {lesson.tags.map((tag, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                </section>
              )}
              <div className="text-xs text-gray-400 pt-3 border-t border-gray-100">
                Project: <span className="text-gray-600">{lesson.project}</span> · Recorded {lesson.date}
              </div>
            </div>
          </div>
        </div>
      )
    })()}

    {/* Create Lesson Modal */}
    {showCreateModal && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Add New Lesson</h2>
            <button
              onClick={() => { setShowCreateModal(false); setCreateForm(EMPTY_FORM); setTagInput('') }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          <form onSubmit={handleCreateSubmit} className="p-6 space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={createForm.title}
                onChange={e => setCreateForm(p => ({ ...p, title: e.target.value }))}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#334E68] text-gray-900"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description <span className="text-red-500">*</span></label>
              <textarea
                value={createForm.description}
                onChange={e => setCreateForm(p => ({ ...p, description: e.target.value }))}
                required
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#334E68] resize-none text-gray-900"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={createForm.type || ''}
                  onChange={e => setCreateForm(p => ({ ...p, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#334E68] text-gray-900"
                >
                  <option value="">Select type...</option>
                  <option value="success">Success Story</option>
                  <option value="challenge">Challenge</option>
                  <option value="improvement">Improvement</option>
                  <option value="innovation">Innovation</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={createForm.category || ''}
                  onChange={e => setCreateForm(p => ({ ...p, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#334E68] text-gray-900"
                >
                  <option value="">Select category...</option>
                  <option value="technical">Technical</option>
                  <option value="process">Process</option>
                  <option value="communication">Communication</option>
                  <option value="risk">Risk</option>
                  <option value="resource">Resource</option>
                  <option value="procurement">Procurement</option>
                  <option value="quality">Quality</option>
                  <option value="schedule">Schedule</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phase</label>
                <input
                  type="text"
                  value={createForm.phase || ''}
                  onChange={e => setCreateForm(p => ({ ...p, phase: e.target.value }))}
                  placeholder="e.g. Initiation, Execution"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#334E68] text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Impact Score (1–10)</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={createForm.impact || ''}
                  onChange={e => setCreateForm(p => ({ ...p, impact: e.target.value ? parseInt(e.target.value) : undefined }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#334E68] text-gray-900"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={createForm.status || 'shared'}
                  onChange={e => setCreateForm(p => ({ ...p, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#334E68] text-gray-900"
                >
                  <option value="shared">Shared</option>
                  <option value="implemented">Implemented</option>
                  <option value="action-pending">Action Pending</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project (optional)</label>
                <select
                  value={createForm.project_id?.toString() || ''}
                  onChange={e => setCreateForm(p => ({ ...p, project_id: e.target.value ? parseInt(e.target.value) : undefined }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#334E68] text-gray-900"
                >
                  <option value="">No project</option>
                  {projects.map(p => <option key={p.id} value={p.id.toString()}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action Taken</label>
              <textarea
                value={createForm.action_taken || ''}
                onChange={e => setCreateForm(p => ({ ...p, action_taken: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#334E68] resize-none text-gray-900"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Benefits Realized</label>
              <textarea
                value={createForm.benefits_realized || ''}
                onChange={e => setCreateForm(p => ({ ...p, benefits_realized: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#334E68] resize-none text-gray-900"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
              <div className="flex gap-2 mb-2 flex-wrap">
                {(createForm.tags || []).map(tag => (
                  <span key={tag} className="flex items-center gap-1 px-2 py-1 bg-[#334E68] text-white text-xs rounded-full">
                    {tag}
                    <button type="button" onClick={() => removeTag(tag)} className="ml-1 hover:text-red-300">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag() } }}
                  placeholder="Add tag and press Enter"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#334E68] text-gray-900"
                />
                <button type="button" onClick={addTag} className="px-3 py-2 bg-[#334E68] text-white rounded-lg hover:bg-[#486581] text-sm">
                  Add
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => { setShowCreateModal(false); setCreateForm(EMPTY_FORM); setTagInput('') }}
                className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2 bg-[#DAA520] text-white rounded-lg hover:bg-[#c99a1c] disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Plus className="w-4 h-4" />}
                Add Lesson
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
    </>
  )
}

